/**
 * Batch 8 · iter_161 — AI アシスタント設定 page.
 *
 * Lives under Settings → AI アシスタント (per-user page, not admin-only).
 * Owns three preference dropdowns + an enable-toggle + a "Forget me"
 * button. Also shows a scrubbed status panel of which provider is
 * active per feature (from GET /api/ai/status). Never exposes the on-
 * prem URL or the Emergent LLM key.
 *
 * Consumed later by:
 *   * Batch 9 · Knowledge AI writing assistant  (feature = knowledge)
 *   * Batch 10 · Chat thinking assistant        (feature = chat)
 *
 * A user's prefs are language / tone / detail — the writing style
 * knobs the assistant will honor when it responds. Retention: kept
 * forever until the user clicks 「AI設定を初期化」. No prompt or
 * response is stored anywhere.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { TranslationHealthSection } from '@/pages/settings/aiconsole/TranslateSection';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Robot, Trash, CheckCircle, XCircle, GlobeHemisphereWest, BookOpen, ArrowSquareOut, HardDrives } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGE_OPTS = [
  { value: 'auto', label: '自動(ユーザー入力に合わせる)' },
  { value: 'ja',   label: '日本語で返答' },
  { value: 'en',   label: 'English で返答' },
];
const TONE_OPTS = [
  { value: 'polite',  label: '丁寧・敬語' },
  { value: 'neutral', label: '中立・ビジネス' },
  { value: 'casual',  label: 'カジュアル・親しみやすい' },
];
const DETAIL_OPTS = [
  { value: 'concise',  label: '簡潔(要点のみ)' },
  { value: 'normal',   label: '標準' },
  { value: 'thorough', label: '詳細(例を交えて)' },
];
const FEATURE_META = {
  knowledge: { label: 'ナレッジ執筆アシスタント', Icon: BookOpen, accent: 'text-indigo-600' },
};

function Row({ label, hint, children }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-3 md:gap-6 py-4 border-b border-zinc-200 last:border-b-0">
      <div>
        <div className="text-sm font-medium text-zinc-800">{label}</div>
        {hint && <div className="mt-1 text-xs text-zinc-500 leading-relaxed">{hint}</div>}
      </div>
      <div className="flex items-center">{children}</div>
    </div>
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full max-w-md rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function ProviderPill({ feat, cfg }) {
  const meta = FEATURE_META[feat] || { label: feat, Icon: Robot, accent: 'text-zinc-600' };
  const Icon = meta.Icon;
  const configured = !!cfg?.configured;
  return (
    <div className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-zinc-50/60 px-3 py-2.5" data-testid={`ai-status-${feat}`}>
      <div className={`shrink-0 rounded-md bg-white p-1.5 ${meta.accent}`}>
        <Icon size={16} weight="fill" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-zinc-800 truncate">{meta.label}</div>
        <div className="mt-0.5 text-[11px] text-zinc-500 truncate">
          {configured ? (
            cfg.scheme === 'emergent'
              ? `Emergent · ${cfg.provider} / ${cfg.model}`
              : `オンプレ · ${cfg.host || 'ローカル'}`
          ) : (
            cfg?.reason || cfg?.error || '未設定'
          )}
        </div>
      </div>
      {configured ? (
        <CheckCircle size={16} weight="fill" className="shrink-0 text-emerald-500" />
      ) : (
        <XCircle size={16} weight="fill" className="shrink-0 text-zinc-300" />
      )}
    </div>
  );
}

function MemoryCard() {
  const [facts, setFacts] = useState(null);
  const load = () => axios.get(`${API}/assistant/memory`).then((r) => setFacts(r.data.facts || [])).catch(() => setFacts([]));
  useEffect(() => { load(); }, []);
  const removeFact = async (f) => {
    try { await axios.delete(`${API}/assistant/memory/${f.id}`); load(); } catch { toast.error('削除に失敗しました'); }
  };
  const clearAll = async () => {
    if (!window.confirm('AIが記憶したすべての情報を削除しますか？')) return;
    try { await axios.delete(`${API}/assistant/memory`); load(); toast.success('メモリを削除しました'); } catch { toast.error('削除に失敗しました'); }
  };
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm" data-testid="ai-memory-card">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-1.5 text-sm font-bold text-zinc-800">
            <BookOpen size={16} weight="duotone" className="text-violet-600" /> AIの記憶（会話メモリ）
          </h3>
          <p className="mt-0.5 text-xs text-zinc-500">アシスタントが会話から学習した、あなたに関する情報です。回答のパーソナライズに使用されます。</p>
        </div>
        {facts?.length > 0 && (
          <button type="button" onClick={clearAll} data-testid="ai-memory-clear"
            className="shrink-0 min-h-[32px] rounded-lg border border-rose-200 px-2.5 py-1.5 text-[11px] font-semibold text-rose-600 hover:bg-rose-50 transition-colors">
            すべて削除
          </button>
        )}
      </div>
      <div className="mt-3 space-y-1.5">
        {facts === null ? (
          <p className="text-xs text-zinc-400">読み込み中…</p>
        ) : facts.length === 0 ? (
          <p className="rounded-lg border border-dashed border-zinc-200 px-3 py-4 text-center text-[11px] text-zinc-400" data-testid="ai-memory-empty">
            まだ記憶はありません — AIアシスタントと会話すると、役立つ情報が自動的に記憶されます
          </p>
        ) : facts.map((f) => (
          <div key={f.id} className="group flex items-center gap-2 rounded-lg border border-zinc-100 bg-zinc-50/50 px-3 py-1.5" data-testid={`ai-memory-fact-${f.id}`}>
            <span className="flex-1 text-[12px] text-zinc-700">{f.text}</span>
            <span className="text-[9.5px] text-zinc-300">{(f.ts || '').slice(5, 10)}</span>
            <button type="button" onClick={() => removeFact(f)} data-testid={`ai-memory-del-${f.id}`}
              aria-label={`この記憶を削除（${f.text}）`}
              title="この記憶を削除"
              className="relative rounded p-1 text-zinc-300 opacity-0 transition-opacity after:absolute after:-inset-1.5 after:content-[''] group-hover:opacity-100 focus-visible:opacity-100 hover:bg-rose-50 hover:text-rose-500">
              <Trash size={12} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// AI実行元ステータス（読み取り専用）— プロバイダー設定の変更は
// AI管理コンソールが唯一の管理場所（single source of truth）。
function RuntimeSourceCard() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    axios.get(`${API}/ai/provider`, { withCredentials: true })
      .then((r) => setCfg(r.data || {}))
      .catch(() => setCfg({}));
  }, []);
  const isCustom = cfg?.mode === 'custom';
  const rows = cfg ? [
    ['現在のプロバイダー', isCustom ? 'ローカルLLM（自社サーバー）' : 'Emergent（既定）'],
    ...(isCustom && cfg.base_url ? [['エンドポイント', cfg.base_url]] : []),
    ['テキストモデル', cfg.text_model || '（サーバー既定）'],
    ['画像モデル（Vision）', cfg.vision_model || '（未設定）'],
    ['OCR・画像処理', isCustom && cfg.use_for_vision ? 'ローカルLLMを使用' : 'Emergent（Gemini Vision）を使用'],
    ['設定元', 'AI管理コンソール'],
  ] : [];
  return (
    <div className="rounded-xl border border-zinc-200 bg-white shadow-sm" data-testid="ai-runtime-source-card">
      <div className="flex items-center gap-3 border-b border-zinc-200 px-6 py-4">
        <div className="rounded-lg bg-violet-50 p-2 text-violet-600">
          <HardDrives size={20} weight="fill" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-zinc-900">AI実行元（読み取り専用）</h2>
          <p className="mt-0.5 text-xs text-zinc-500">
            AIプロバイダー設定は「AI管理コンソール」で一元管理されています。このページからは変更できません。
          </p>
        </div>
        <Link
          to="/settings/ai-console"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-violet-700"
          data-testid="ai-runtime-open-console"
        >
          <ArrowSquareOut size={14} weight="bold" />
          AI管理コンソールを開く
        </Link>
      </div>
      <div className="px-6 py-4">
        {cfg === null ? (
          <p className="text-xs text-zinc-400">読み込み中…</p>
        ) : (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
            {rows.map(([k, v]) => (
              <div key={k} className="flex items-baseline gap-2 text-[12.5px]">
                <dt className="w-36 shrink-0 text-zinc-500">{k}</dt>
                <dd className="min-w-0 truncate font-medium text-zinc-800" title={v}>{v}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  );
}

export default function AiPreferencesSettings() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(null);   // null while loading
  const [status, setStatus] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const [p, s] = await Promise.all([
        axios.get(`${API}/ai/prefs`),
        axios.get(`${API}/ai/status`),
      ]);
      setPrefs(p.data);
      setStatus(s.data?.features || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || '読み込みに失敗しました');
    }
  };

  useEffect(() => { load(); }, []);

  const update = (patch) => {
    setPrefs((cur) => ({ ...(cur || {}), ...patch }));
    setDirty(true);
  };

  const save = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      const body = {
        language: prefs.language,
        tone:     prefs.tone,
        detail:   prefs.detail,
        enabled:  prefs.enabled,
      };
      const r = await axios.put(`${API}/ai/prefs`, body);
      setPrefs(r.data);
      setDirty(false);
      toast.success('AI設定を保存しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const forget = async () => {
    if (!window.confirm('AI設定を初期状態に戻します。よろしいですか？')) return;
    setSaving(true);
    try {
      const r = await axios.delete(`${API}/ai/prefs`);
      setPrefs(r.data);
      setDirty(false);
      toast.success('AI設定を初期化しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '初期化に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) {
    return <div className="rounded-xl border border-zinc-200 bg-white p-8 text-sm text-zinc-500" data-testid="ai-prefs-loading">読み込み中…</div>;
  }

  return (
    <div className="space-y-6" data-testid="ai-preferences-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900"
          data-testid="settings-page-title">アシスタントの設定</h1>
        <p className="text-sm text-zinc-500 mt-1">
          ナレッジ執筆アシスタントの応答スタイルを決めます。ここでの変更は文章の書き方だけに影響し、書類やデータは変わりません。
        </p>
      </div>
      <div className="rounded-xl border border-zinc-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-zinc-200 px-6 py-4">
          <div className="rounded-lg bg-indigo-50 p-2 text-indigo-600">
            <Robot size={20} weight="fill" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-zinc-900">AI アシスタント設定</h2>
            <p className="mt-0.5 text-xs text-zinc-500">
              ナレッジ執筆アシスタントで使用する応答スタイルを設定します。プロンプトや返答はサーバーに保存されません。
            </p>
          </div>
        </div>

        <div className="px-6 py-2">
          <Row label="応答言語" hint="モデルの返答に使用する言語です。「自動」ではユーザーの入力に合わせて切り替わります。">
            <Select value={prefs.language} onChange={(v) => update({ language: v })} options={LANGUAGE_OPTS} testid="ai-pref-language" />
          </Row>
          <Row label="口調" hint="ビジネス文書には「丁寧」、社内チャットには「カジュアル」が読みやすくなります。">
            <Select value={prefs.tone} onChange={(v) => update({ tone: v })} options={TONE_OPTS} testid="ai-pref-tone" />
          </Row>
          <Row label="詳しさ" hint="要点だけ知りたい場合は「簡潔」、初めての操作を学ぶ場合は「詳細」を選んでください。">
            <Select value={prefs.detail} onChange={(v) => update({ detail: v })} options={DETAIL_OPTS} testid="ai-pref-detail" />
          </Row>
          <Row label="アシスタントを有効にする" hint="無効にすると、ナレッジ執筆のアシスタントボタン自体が表示されません。">
            <div className="flex items-center gap-3">
              <Switch checked={!!prefs.enabled} onCheckedChange={(v) => update({ enabled: !!v })} data-testid="ai-pref-enabled" aria-label="アシスタントを有効にする" />
              <span className="text-sm text-zinc-600">{prefs.enabled ? '有効' : '無効'}</span>
            </div>
          </Row>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-zinc-200 bg-zinc-50/70 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={forget}
            disabled={saving}
            className="inline-flex min-h-[32px] items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-zinc-500 transition-colors hover:bg-white hover:text-rose-600 disabled:opacity-50"
            data-testid="ai-pref-forget"
          >
            <Trash size={13} /> AI設定を初期化
          </button>
          <div className="flex items-center gap-3">
            {dirty && <span className="text-xs text-amber-600">未保存の変更があります</span>}
            <Button onClick={save} disabled={saving || !dirty} data-testid="ai-pref-save">
              {saving ? '保存中…' : '保存'}
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-zinc-200 px-6 py-4">
          <div className="rounded-lg bg-zinc-100 p-2 text-zinc-600">
            <GlobeHemisphereWest size={20} weight="fill" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-zinc-900">プロバイダ状態</h2>
            <p className="mt-0.5 text-xs text-zinc-500">
              現在アクティブな AI モデルを機能別に表示しています。切り替えは管理者による環境変数の設定が必要です。
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 px-6 py-5 sm:grid-cols-2">
          {['knowledge'].map((feat) => (
            <ProviderPill key={feat} feat={feat} cfg={status[feat]} />
          ))}
        </div>
      </div>

      <MemoryCard />
      {(user?.role === 'admin' || user?.role === 'manager') && (
        <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm"
          data-testid="translation-health-card">
          <h2 className="text-base font-semibold text-zinc-900">英語表示（翻訳）の状態</h2>
          <p className="mb-3 mt-0.5 text-xs text-zinc-500">
            上部バーの「EN」はローカルLLMが日本語から生成した表示です。日本語が正本で、翻訳が失敗した場合は日本語のまま表示されます。
          </p>
          <TranslationHealthSection readOnly={user?.role !== 'admin'} />
        </div>
      )}
      {user?.role === 'admin' && <RuntimeSourceCard />}
    </div>
  );
}
